import React, { useEffect, useMemo, useState } from 'react';
import {
  fetchHospitalityInquiries,
  type HospitalityInquiry,
} from '../../api/hospitalityInquiries';
import CampaignAttributionDetails from './CampaignAttributionDetails';

function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

const HospitalityInquiriesSection: React.FC<{ userId: number }> = ({ userId }) => {
  const [rows, setRows] = useState<HospitalityInquiry[]>([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<HospitalityInquiry | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const list = await fetchHospitalityInquiries(userId);
        if (!cancelled) setRows(list);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load inquiries.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter((row) =>
      `${row.first_name} ${row.last_name} ${row.company} ${row.email} ${row.phone} ${(row.workflows ?? []).join(' ')}`
        .toLowerCase()
        .includes(term)
    );
  }, [rows, search]);

  return (
    <section className="events-table-section" aria-labelledby="hospitality-heading">
      <h2 className="events-section-title" id="hospitality-heading">
        Hospitality <span className="events-section-count">({rows.length})</span>
      </h2>
      <p className="page-subtitle website-lead-intro">
        Submissions from the hospitality workflow form on the website.
      </p>
      {error ? (
        <p className="create-order-error" role="alert">
          {error}
        </p>
      ) : null}
      <div className="website-lead-controls">
        <input
          type="search"
          className="website-lead-search"
          placeholder="Search name, company, email…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search hospitality inquiries"
        />
      </div>
      {loading ? (
        <p className="page-subtitle">Loading…</p>
      ) : (
        <div className="events-table-wrapper">
          <table className="tasks-search-results-table">
            <thead>
              <tr>
                <th scope="col">Name</th>
                <th scope="col">Company</th>
                <th scope="col">Email</th>
                <th scope="col">Phone</th>
                <th scope="col">Workflows</th>
                <th scope="col">SMS consent</th>
                <th scope="col">Received</th>
              </tr>
            </thead>
            <tbody>
              {visible.length === 0 ? (
                <tr>
                  <td colSpan={7} className="events-empty-cell">
                    No hospitality inquiries yet.
                  </td>
                </tr>
              ) : (
                visible.map((row) => (
                  <tr key={row.id} onClick={() => setSelected(row)} style={{ cursor: 'pointer' }}>
                    <td data-label="Name">{`${row.first_name} ${row.last_name}`.trim() || '—'}</td>
                    <td data-label="Company">{row.company || '—'}</td>
                    <td data-label="Email">{row.email || '—'}</td>
                    <td data-label="Phone">{row.phone || '—'}</td>
                    <td data-label="Workflows">{(row.workflows ?? []).join(', ') || '—'}</td>
                    <td data-label="SMS consent">{row.sms_marketing_consent ? 'Yes' : 'No'}</td>
                    <td data-label="Received">{formatDateTime(row.created_at)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
      {selected ? (
        <div className="website-lead-detail" style={{ marginTop: 16 }}>
          <h3 className="website-lead-subheading">
            {`${selected.first_name} ${selected.last_name}`.trim() || selected.company || 'Inquiry'}
          </h3>
          <dl className="website-lead-details">
            <div>
              <dt>Company / property</dt>
              <dd>{selected.company || '—'}</dd>
            </div>
            <div>
              <dt>Email</dt>
              <dd>{selected.email || '—'}</dd>
            </div>
            <div>
              <dt>Phone</dt>
              <dd>{selected.phone || '—'}</dd>
            </div>
            <div>
              <dt>SMS marketing consent</dt>
              <dd>{selected.sms_marketing_consent ? 'Yes' : 'No'}</dd>
            </div>
            <div>
              <dt>Workflows</dt>
              <dd>{(selected.workflows ?? []).join(', ') || '—'}</dd>
            </div>
          </dl>
          <CampaignAttributionDetails row={selected} />
          <button type="button" className="secondary-button" onClick={() => setSelected(null)}>
            Close
          </button>
        </div>
      ) : null}
    </section>
  );
};

export default HospitalityInquiriesSection;
